/**
 * The per-device call choices, and the one place the key is written.
 *
 * Microphone, camera, speaker, the three audio constraints, the push-to-talk
 * key and the background. All of them belong to this browser rather than to the
 * account — a speaker is a thing on a desk — so they live in `localStorage` and
 * never go near the server.
 *
 * Settings writes them; the call controller reads them when it takes a seat, so
 * a microphone chosen on a quiet afternoon is the microphone the next call
 * opens. Two copies of the key is how that stops being true, which is why this
 * file exists rather than a constant in each.
 *
 * Storage can be blocked, cleared or hold something an older build wrote, so
 * every read falls back to the defaults and every write is allowed to fail: the
 * choice lasts the page instead.
 */

import type { Background } from './types';

const KEY = 'mauvely.relay.call';

export interface CallPrefs {
  micId: string;
  camId: string;
  speakerId: string;
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGainControl: boolean;
  pushToTalkKey: string;
  background: Background;
}

export const DEFAULT_PREFS: CallPrefs = {
  micId: '',
  camId: '',
  speakerId: '',
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
  pushToTalkKey: '',
  background: { kind: 'none' }
};

export function readPrefs(): CallPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<CallPrefs>) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writePrefs(prefs: CallPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // The choice lasts this page instead.
  }
}

/** The device ids a join takes, with the empty ones left out — an empty string
 *  is "the system default", and passing it as a constraint asks for a device
 *  with no id. */
export function joinDevices(prefs: CallPrefs): { micId?: string; camId?: string; speakerId?: string } {
  return {
    ...(prefs.micId ? { micId: prefs.micId } : {}),
    ...(prefs.camId ? { camId: prefs.camId } : {}),
    ...(prefs.speakerId ? { speakerId: prefs.speakerId } : {})
  };
}
